import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { cssTransition, ToastContainer } from 'react-toastify';
import { useSidebarCollapse } from './zustand/sidebar-collapse';
import AppErrorBoundary from './components/appErrorBoundary';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { setupInterceptors } from './middlewares/api-interceptore';
import ProtectedRoute from './middlewares/protected-routes-middle-ware';
import PublicRoute from './middlewares/public-middle-ware';
import ClinicOnboardingGuard from './middlewares/clinic-onboarding-guard';
import { PermissionRouteGuard } from './middlewares/permission-route-guard';
import useRoutes from './hooks/useRoutes';
import LoadingElement from './components/shared/loadingElement';
import { getHomePath } from './utils/helpers';
import { loadRemainingNamespaces, changeAppLanguage } from './i18n';
import { useLanguageTransitionStore } from './zustand/language-transition-store';
import { useAuth } from './zustand/auth-store';
import { useSummaryQueries } from './apis/summary/query';
import { identifyUser, resetAnalyticsUser, setTrackingContext } from './services/analytics';
import { useUserType } from './zustand/user-type-store';
import { useRetentionSignal } from './hooks/useRetentionSignal';
import 'twin.macro';

const LayoutSection = lazy(() => import('@/components/layoutSection'));

function SessionSync() {
  const { accessToken, user } = useAuth();
  const { refetch } = useSummaryQueries.useGetMe({ enabled: !!accessToken });
  const { i18n } = useTranslation();
  const syncedRef = useRef(false);
  const [identityReady, setIdentityReady] = useState(false);
  useRetentionSignal(identityReady);

  useEffect(() => {
    if (!accessToken) {
      if (syncedRef.current) resetAnalyticsUser();
      syncedRef.current = false;
      setIdentityReady(false);
      return;
    }
    if (syncedRef.current) {
      return;
    }

    refetch()
      .then(({ data }) => {
        if (syncedRef.current) {
          return;
        }
        syncedRef.current = true;
        if (!data) {
          return;
        }

        // Language sync
        const rawLang = data?.language;
        if (rawLang) {
          const langCode = (
            typeof rawLang === 'string' ? rawLang : (rawLang?.code ?? rawLang?.value ?? '')
          )
            .toLowerCase()
            .split('-')[0];
          const current = (i18n.resolvedLanguage || i18n.language || 'en')
            .split('-')[0]
            .toLowerCase();
          if (langCode && langCode !== current) changeAppLanguage(langCode);
        }

        // User identity
        if (user?.id) {
          const fullName =
            [data.firstName, data.lastName].filter(Boolean).join(' ') || data.fullName;
          const langCode = rawLang
            ? (typeof rawLang === 'string' ? rawLang : (rawLang?.code ?? rawLang?.value ?? ''))
                .toLowerCase()
                .split('-')[0]
            : undefined;
          const userType = useUserType.getState().userType;
          // Excluded-from-analytics flags are sent as their own event properties
          // instead of blocking tracking outright, so excluded users still show up
          // but can be filtered. Both are sent regardless of user type — /me only
          // ever populates the one matching the logged-in user's own type.
          const exclusionProps = {
            patient_excluded: data.patientExcluded === true,
            therapist_excluded: data.therapistExcluded === true,
          };
          identifyUser(user.id, {
            $name: fullName || undefined,
            $email: data.email || undefined,
            user_type: userType,
            ...exclusionProps,
            ...(langCode ? { language: langCode } : {}),
          });
          setTrackingContext({
            user_type: userType,
            ...exclusionProps,
          });
          setIdentityReady(true);
        }
      })
      .catch(() => {});
  }, [accessToken]);

  return null;
}

const AppRoutes = () => {
  const { isCollapsed, setIsCollapsed } = useSidebarCollapse();
  const location = useLocation();
  const navigate = useNavigate();
  const { privateRoute, puplicRoute, sherdRoute, protectedStandaloneRoute } = useRoutes();
  const { t } = useTranslation(['layout', 'common']);

  useEffect(() => {
    setupInterceptors(navigate, t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    loadRemainingNamespaces();
  }, []);

  const urlParams = new URLSearchParams(location.search);
  // Normalize malformed links where secondary params are joined with ? instead of &
  // e.g. ?tab=Activity?sessionId=249&token=x → ?tab=Activity&sessionId=249&token=x
  const tabParam = urlParams.get('tab');
  if (tabParam?.includes('?')) {
    const splitIdx = tabParam.indexOf('?');
    urlParams.set('tab', tabParam.substring(0, splitIdx));
    new URLSearchParams(tabParam.substring(splitIdx + 1)).forEach((val, key) => {
      if (!urlParams.has(key)) {
        urlParams.set(key, val);
      }
    });
  }
  const embeddedToken = urlParams.get('token');
  const accessToken = useAuth.getState().accessToken;
  if (!accessToken && embeddedToken && location.pathname !== '/auth/magic-link') {
    urlParams.delete('token');
    const cleanSearch = urlParams.toString();
    const redirectPath = location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
    const to = `/auth/magic-link?token=${encodeURIComponent(embeddedToken)}&redirect=${encodeURIComponent(redirectPath)}`;
    return <Navigate to={to} replace />;
  }

  return (
    <Suspense
      fallback={
        <div tw="flex items-center justify-center min-h-screen">
          <LoadingElement width={80} height={80} />
        </div>
      }
    >
      <Routes>
        <Route element={<PublicRoute />}>
          {puplicRoute.map(route => (
            <Route path={route.route} element={route.element} key={route.route} />
          ))}
        </Route>
        <Route element={<ProtectedRoute />}>
          {protectedStandaloneRoute.map(route => (
            <Route path={route.route} element={route.element} key={route.route} />
          ))}
        </Route>
        {sherdRoute.map(item => (
          <Route element={item.element} key={item.route} path={item.route} />
        ))}
        <Route
          path="/"
          element={
            <LayoutSection
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              currentRoute={location.pathname}
            />
          }
        >
          <Route element={<ProtectedRoute />}>
            <Route element={<ClinicOnboardingGuard />}>
              <Route index element={<Navigate to={getHomePath()} replace />} />
              {privateRoute.map(route => (
                <Route
                  key={route.route}
                  path={route.route}
                  element={
                    route.requiredPermission ? (
                      <PermissionRouteGuard requiredPermission={route.requiredPermission}>
                        {route.element}
                      </PermissionRouteGuard>
                    ) : (
                      route.element
                    )
                  }
                >
                  {route.children &&
                    route.children.map(child => (
                      <Route key={child.route} path={child.route} element={child.element} />
                    ))}
                </Route>
              ))}
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  const { i18n } = useTranslation();
  const isChangingLanguage = useLanguageTransitionStore(state => state.isChangingLanguage);
  const appRenderKey = `${i18n.resolvedLanguage || i18n.language}-${i18n.dir()}`;

  useEffect(() => {
    const handlePreloadError = () => window.location.reload();
    window.addEventListener('vite:preloadError', handlePreloadError);
    return () => window.removeEventListener('vite:preloadError', handlePreloadError);
  }, []);

  // Define fade transition
  const Fade = cssTransition({
    enter: 'fadeIn', // Uses CSS class `.fadeIn`
    exit: 'fadeOut', // Uses CSS class `.fadeOut`
    duration: [3, 3], // [enter duration, exit duration]
  });

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <SessionSync />
        <ToastContainer transition={Fade} autoClose={3000} style={{ zIndex: 9999 }} />
        {isChangingLanguage && (
          <div tw="fixed inset-0 z-[10000] flex items-center justify-center bg-[#F5F5F5]">
            <LoadingElement width={80} height={80} />
          </div>
        )}
        <div key={appRenderKey} tw="w-full overflow-x-hidden">
          <AppRoutes />
        </div>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
